// GrapesJS + grapesjs-mjml editor initializer.
//
// Why this file: we replaced easy-email-editor with grapesjs at /editor (Neugence
// Marketing). Backend (/saveusertemplate, /mjml2html, /emailcampaign) still reads
// MJML strings, so the editor must round-trip MJML — this module owns that contract.
//
// Plugin API discovered in node_modules/grapesjs-mjml/dist/index.js:
//   - 'mjml-code'         -> returns current document as an MJML string (programmatic, no UI)
//   - 'mjml-code-to-html' -> compiles MJML to HTML (returns { html, errors })
//   - 'mjml-import'       -> opens an import-modal (UI). Internally it calls
//                            editor.setComponents(mjml) — so for programmatic load
//                            we just call editor.setComponents() ourselves.
//   - 'mjml-export'       -> opens an export-modal (UI, not used here)
//
// We deliberately do NOT use @grapesjs/react (it's React 18 only and this app is
// React 17). Vanilla mount via DOM element works fine.

import grapesjs from 'grapesjs';
import grapesjsMjml from 'grapesjs-mjml';
import 'grapesjs/dist/css/grapes.min.css';

const DEFAULT_MJML = `<mjml>
  <mj-body>
    <mj-section>
      <mj-column>
        <mj-text font-size="20px" align="center">Start editing your email</mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

// Per-recipient personalization tags. The editor exposes these in two places
// (BlockManager drag-and-drop block + RichTextEditor inline toolbar button),
// and the backend (emailfrontend `app_ses_routes.substitute_merge_tags`)
// substitutes them at send time using the recipient's contacts row + a small
// set of built-ins (`email`, `unsubscribe_url`).
//
// Keep the inserted text PLAIN (`<span>{{first_name}}</span>`) so the
// exported MJML does not bake editor-only styling into the customer-visible
// email. Visually-distinguishing pills can come later via a canvas-only
// CSS rule if needed; the literal `{{first_name}}` is already obvious enough.
export const MERGE_TAGS = [
  { tag: 'first_name', label: 'First name' },
  { tag: 'last_name', label: 'Last name' },
  { tag: 'email', label: 'Email' },
  { tag: 'company', label: 'Company' },
  { tag: 'position', label: 'Position' },
  { tag: 'unsubscribe_url', label: 'Unsubscribe URL' },
];

// Phosphor "tag" SVG (inline so we don't pull in a React icon component into
// this vanilla module). 16x16, currentColor stroke.
const MERGE_TAG_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="currentColor"><path d="M243.31,136,144,36.69A15.86,15.86,0,0,0,132.69,32H40a8,8,0,0,0-8,8v92.69A15.86,15.86,0,0,0,36.69,144L136,243.31a16,16,0,0,0,22.63,0l84.68-84.68a16,16,0,0,0,0-22.63ZM84,96A12,12,0,1,1,96,84,12,12,0,0,1,84,96Z"/></svg>`;

/**
 * Register the merge-tag UI on a live grapesjs editor instance.
 *   - Adds one block per tag under the "Merge tags" category in the left
 *     panel BlockManager. Drag onto the canvas → inserts a text component
 *     containing the literal `{{tag}}` string.
 *   - Adds one RichTextEditor button per tag, so users editing inline text
 *     inside an existing block can also insert via the floating RTE toolbar.
 *
 * Idempotent: callers may invoke this on every editor 'load' without
 * registering duplicates; BlockManager.add / RichTextEditor.add overwrite
 * by id.
 */
function registerMergeTags(editor) {
  const bm = editor.BlockManager;
  const rte = editor.RichTextEditor;
  for (const { tag, label } of MERGE_TAGS) {
    const placeholder = `{{${tag}}}`;
    if (bm && typeof bm.add === 'function') {
      bm.add(`merge-tag-${tag}`, {
        label,
        category: { id: 'merge-tags', label: 'Merge tags' },
        media: MERGE_TAG_ICON_SVG,
        // Insert as a plain text component containing the literal merge tag.
        // grapesjs wraps loose strings in a text node; using a `<span>` keeps
        // it inline so it doesn't break a paragraph the user drops it into.
        content: `<span data-merge-tag="${tag}">${placeholder}</span>`,
        attributes: { title: `Insert ${placeholder}` },
      });
    }
    if (rte && typeof rte.add === 'function') {
      rte.add(`merge-tag-${tag}`, {
        name: `merge-tag-${tag}`,
        icon: `<span style="font-family:monospace;font-size:11px;">{{${tag.length > 6 ? tag.slice(0, 6) + '…' : tag}}}</span>`,
        attributes: { title: `Insert ${placeholder}` },
        result: (rteInst) => {
          // execCommand('insertText') is the cross-browser way to insert
          // plain text at the caret inside grapesjs's contentEditable RTE.
          if (rteInst && typeof rteInst.exec === 'function') {
            rteInst.exec('insertText', placeholder);
          } else if (rteInst && typeof rteInst.insertHTML === 'function') {
            rteInst.insertHTML(placeholder);
          }
        },
      });
    }
  }
}

// Templates created in the legacy easy-email editor embed non-standard tags
// like <mj-html-attribute class="easy-email" ...> and a custom <mj-attributes>
// block with class="easy-email" markers. grapesjs-mjml's strict parser drops
// these unknown nodes silently — and worse, sometimes the surrounding
// structure depends on them, leaving the canvas blank. Strip the offenders so
// the body content (sections, columns, text, images) renders directly.
function sanitizeLegacyMjml(mjml) {
  if (!mjml || typeof mjml !== 'string') return mjml;
  let out = mjml;
  // Drop the entire <mj-html-attributes> block (easy-email's metadata bag).
  out = out.replace(/<mj-html-attributes[\s\S]*?<\/mj-html-attributes>/gi, '');
  // Drop any stray <mj-html-attribute .../> self-closing nodes.
  out = out.replace(/<mj-html-attribute\b[^>]*\/?>/gi, '');
  // easy-email's class="easy-email" attribute is informational — strip it
  // so grapesjs-mjml doesn't try to interpret it as a CSS class.
  out = out.replace(/\sclass="easy-email"/gi, '');
  // Some legacy templates wrap content in a redundant <mj-wrapper> with
  // attributes that grapesjs-mjml doesn't fully respect. Leave structure intact
  // but unconditionally trim leading/trailing whitespace.
  return out.trim();
}

/**
 * Mount a grapesjs+grapesjs-mjml editor into a DOM element.
 *
 * Also registers the per-recipient merge-tag UI: a "Merge tags" block
 * category in the left panel ({{first_name}}, {{last_name}}, {{email}},
 * {{company}}, {{position}}, {{unsubscribe_url}}) plus matching buttons on
 * the inline RichTextEditor toolbar. The literal `{{tag}}` strings are
 * inserted into the MJML; backend substitution lives in
 * `emailfrontend/server/app_py_sub_paths/mailchampRoutes/app_ses_routes.py`
 * (`substitute_merge_tags`).
 *
 * @param {HTMLElement} element - container to mount into (must have an
 *                                established height/width — grapesjs uses
 *                                height: '100%')
 * @param {object}    [opts]
 * @param {string}    [opts.initialMjml] - MJML string to load. If empty/falsy,
 *                                         a minimal placeholder template is loaded.
 * @param {function}  [opts.onChange]    - called whenever the document changes;
 *                                         receives the current MJML string.
 * @returns {object} the grapesjs editor instance. Caller is responsible for
 *                   calling `editor.destroy()` on unmount. Useful methods:
 *                     - editor.runCommand('core:undo') / 'core:redo'
 *                     - editor.runCommand('preview') / stopCommand('preview')
 *                     - editor.runCommand('mjml-code')         -> string MJML
 *                     - editor.runCommand('mjml-code-to-html') -> { html, errors }
 *                     - editor.setComponents(mjmlString)        -> replace doc
 */
export function initGrapesjs(element, { initialMjml = '', onChange } = {}) {
  if (!element) {
    throw new Error('initGrapesjs: element is required');
  }

  // eslint-disable-next-line no-console
  console.log('[grapesjs] init called, container size:',
    element.offsetWidth, 'x', element.offsetHeight);

  const editor = grapesjs.init({
    container: element,
    fromElement: false,
    height: '100%',
    width: 'auto',
    storageManager: false,
    assetManager: { upload: false, autoAdd: false },
    // Pass the function as the plugin entry. pluginsOpts is keyed by the
    // plugin's package name (NOT the function reference) per grapesjs docs.
    plugins: [grapesjsMjml],
    pluginsOpts: {
      'grapesjs-mjml': {},
    },
  });

  // Pre-sanitize legacy easy-email-format templates so they render here.
  const sanitized = sanitizeLegacyMjml(initialMjml);
  const startMjml = (sanitized && sanitized.length) ? sanitized : DEFAULT_MJML;

  const loadInitial = () => {
    try {
      // eslint-disable-next-line no-console
      console.log('[grapesjs] loading initial MJML, length:', startMjml.length);
      editor.setComponents(startMjml.trim());
      // eslint-disable-next-line no-console
      console.log('[grapesjs] initial MJML loaded, components:',
        editor.getComponents().length);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[grapesjs] failed to load initial MJML:', err);
    }
  };

  // Always defer to the load event — the MJML parser registers during
  // plugin init which happens during editor.init(); the parser isn't
  // guaranteed wired until 'load' fires.
  editor.on('load', () => {
    // eslint-disable-next-line no-console
    console.log('[grapesjs] load event fired');
    try {
      registerMergeTags(editor);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[grapesjs] failed to register merge tags:', err);
    }
    loadInitial();
  });

  // Notify caller on every meaningful change. Subscribe to the broad 'update'
  // event (fires on add/remove/update + style changes) and fall back to the
  // granular events for safety. Each handler runs `mjml-code` to read the
  // current MJML string.
  if (typeof onChange === 'function') {
    let lastEmitted = '';
    const emit = () => {
      try {
        const mjml = editor.runCommand('mjml-code');
        if (typeof mjml === 'string' && mjml !== lastEmitted) {
          lastEmitted = mjml;
          onChange(mjml);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[grapesjsConfig] mjml-code export failed:', err);
      }
    };

    editor.on(
      'component:add component:remove component:update component:styleUpdate update',
      emit,
    );
  }

  return editor;
}

export default initGrapesjs;
